// 騎士の薙ぎの音の候補 (2026-09-28・ユーザー「騎士の薙ぎでそれ試してみて」)。
//   1回目 a〜e (没・「ゲームっぽくない・フリー素材感」) = 層の組み合わせ違い a 空気 / b 床 / c 鉄 / d 弦 / e 全部。
//   2回目 f〜j (09-29・2Dアクションの記号的な音) = f シュバッ / g シュバッ＋ドッ / h ブォン一体 / i ドシュッ / j シュバッ＋ドッ＋キン。
//   3回目 k〜o (09-29・「矛先の金属はサブで、柄の太めの棒を振っているような音」) = 太い柄の「ブォン」が主役・矛先の「シン」は小さく。
//   4回目 p〜t (09-29・試聴シート3「8 番が近い」) = 加工なしの生成音。p = 8 番そのもの / q・r・s・t = 長く頼んで振りが何回も入った音から一振りずつ切り出した物。
//   pv (09-29・ユーザー「Pが一番近い。薙系の技のフリの大きさや何連目かによって音の大きさと音階？を変えて使っていくのが良さそう」)
//     = P を、技の振りの大きさ・連の何拍目かで大きさと高さを変えて鳴らす (KNIGHT_SWEEP_PV)。高さ違いの音 = scripts/render_knight_sweep_sound_pv.py。
//   adopted = 採用版＝既定。09-29 夕方にユーザー「今日の鳴らし分けに差し替えて」で L → P の振り分け (pv と同じ音・public/sfx/knight_sweep_pv_<役>.wav)。
//   prevL = 前の採用版 L (09-29 昼「Lにする」・public/sfx/knight_sweep.wav・戻す時はこれを adopted に)。now = 旧い手続き音 (knight_sweep_scrape)。
//   音の書き出し = scripts/render_knight_sweep_sound.py (a〜e)・render_knight_sweep_sound_v2.py (f〜j)・_v3.py (k〜o)・_v4.py (p〜t) → public/sfx/candidates/ (git 外)。
//   設計の意図 = docs/departments/sound/2026-09-28-knight-sweep-sound-design.md。
//   off = 薙ぎそのものの音を鳴らさない (09-29 まで配布版はこれだった)。
import type { SfxId } from './types'

export const KNIGHT_SWEEP_CANDIDATES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't'] as const
export type KnightSweepSound = 'adopted' | 'off' | 'now' | 'pv' | 'prevL' | typeof KNIGHT_SWEEP_CANDIDATES[number]
export const KNIGHT_SWEEP_SOUNDS: readonly KnightSweepSound[] = ['adopted', 'off', 'now', 'pv', 'prevL', ...KNIGHT_SWEEP_CANDIDATES]
// 候補ごとの音量の上乗せ (dB)。p〜t は割れを避けて山を -1dBFS に抑えたぶん L より小さく書き出した＝ここで L と同じ大きさ (M400) に戻す。
export const KNIGHT_SWEEP_GAIN_DB: Partial<Record<typeof KNIGHT_SWEEP_CANDIDATES[number], number>> = { p: 0.95, q: 4.28, r: 3.31, s: 3.6, t: 5.99 }
// pv の振り分け (09-29 2回目の値でユーザー「ちょうどいい」→ 配布版に採用)。通常の振りは小さく、大振りだけはっきり (09-29 ユーザーの破戒僧の聴き方)。大きいほど低く、小さく速いほど高く。
//   gainDb = 単発の薙ぎからの差 / semitones = 高さ。sample = public/sfx/ の役ごとの音＝candidates/ の knight_sweep_p (0) と knight_sweep_pv_{m4,m3,p2,p4} (render_knight_sweep_sound_pv.py) を役の名前で写した物。
//   09-29 2回目＝ユーザー「もう少しさがあって良いな」で差を広げた。1回目は cross +2dB/-2・mid -6dB/+1,+2・締め 0dB/-1。
export type KnightSweepRole = 'single' | 'cross' | 'midA' | 'midB' | 'finisher'
export const KNIGHT_SWEEP_PV: Record<KnightSweepRole, { label: string; gainDb: number; semitones: number; sample: string }> = {
  single:   { label: '単発の薙ぎ・連の初段 (溜め0.7秒の大振り)', gainDb: 0, semitones: 0, sample: 'knight_sweep_pv_single' },
  cross:    { label: '跳び下がり薙ぎ (溜め0.9秒・いちばん大きい)', gainDb: 3, semitones: -4, sample: 'knight_sweep_pv_cross' },
  midA:     { label: '連の途中の薙ぎ (溜め0.4秒の小振り・奇数拍)', gainDb: -10, semitones: 2, sample: 'knight_sweep_pv_midA' },
  midB:     { label: '連の途中の薙ぎ (偶数拍・同じ音の連打に聞こえないよう高さを交互に)', gainDb: -10, semitones: 4, sample: 'knight_sweep_pv_midB' },
  finisher: { label: '連の締めの薙ぎ (最後の一振り)', gainDb: 2, semitones: -3, sample: 'knight_sweep_pv_finisher' },
}
// 高さ違いの音を L と同じ大きさ (M400) に戻す量 (dB)。p は +0.95、高さを変えると M400 が ±0.5dB ほど動くぶんを足した。
export const KNIGHT_SWEEP_PV_MATCH_DB: Record<KnightSweepRole, number> = { single: 0.95, cross: 0.03, midA: 1.42, midB: 1.88, finisher: 0.25 }

// 薙ぎ系の技が、振りの大きさと連の中の位置でどの役か。beatIndex/beatCount = 連の何拍目か/拍の数 (連でなければ 0/1)。
export function knightSweepRole(attackId: string, beatIndex = 0, beatCount = 1): KnightSweepRole {
  if (attackId === 'lanceCross') return 'cross'
  if (beatCount <= 1 || beatIndex <= 0) return 'single'
  if (beatIndex >= beatCount - 1) return 'finisher'
  return beatIndex % 2 === 1 ? 'midA' : 'midB'
}

export const KNIGHT_SWEEP_DEFAULT: KnightSweepSound = 'adopted'

let knightSweepSound: KnightSweepSound = KNIGHT_SWEEP_DEFAULT
export function getKnightSweepSound(): KnightSweepSound { return knightSweepSound }
export function setKnightSweepSound(v: KnightSweepSound): void { knightSweepSound = v }
export function parseKnightSweepSound(v: string | null): KnightSweepSound | null {
  return v && (KNIGHT_SWEEP_SOUNDS as readonly string[]).includes(v) ? v as KnightSweepSound : null
}

// 薙ぎ (lanceSweep) と跳び下がり薙ぎ (lanceCross) の当たりの始まりに鳴らす音。off なら null。
export function knightSweepSfx(attackId: string, beatIndex = 0, beatCount = 1): SfxId | null {
  if (knightSweepSound === 'off' || (attackId !== 'lanceSweep' && attackId !== 'lanceCross')) return null
  if (knightSweepSound === 'adopted' || knightSweepSound === 'pv') return `knight_sweep_pv_${knightSweepRole(attackId, beatIndex, beatCount)}`
  if (knightSweepSound === 'prevL') return 'knight_sweep'
  return knightSweepSound === 'now' ? 'knight_sweep_scrape' : `knight_sweep_${knightSweepSound}`
}
